import { useEffect, useRef, useState } from "react";
import { Spinner, UploadSimple, CheckCircle, WarningCircle, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function CustomerImportPanel() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const inputRef = useRef(null);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      // Authenticated `api` axios client — request interceptor attaches
      // the Entra ID Bearer token in production and preserves the
      // preview `X-User-Email` header only when MSAL isn't configured.
      const { data } = await api.get("/customers/import-history");
      setHistory(data || []);
    } catch {
      /* keep prior history; a transient failure shouldn't clear the table */
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const pickFile = () => inputRef.current?.click();

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xls|xlsx)$/i.test(f.name)) {
      toast.error("Please select an .xls or .xlsx file exported from the ERP");
      return;
    }
    setFile(f);
    setSummary(null);
  };

  const runImport = async () => {
    if (!file) return;
    setUploading(true);
    setSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Route the upload through the authenticated `api` axios client
      // (v1.2.2 · 2026-08-19 · P1 fix). The prior raw `fetch()` bypassed
      // the MSAL Bearer-token interceptor, causing 401 "Not signed in"
      // in production where `ALLOW_DEV_EMAIL_HEADER` is disabled.
      //
      // `Content-Type: multipart/form-data` here matches the pattern
      // already used by /price-notifications/upload — axios detects the
      // FormData body and appends the correct boundary itself. Do NOT
      // set a manual boundary.
      const { data } = await api.post("/customers/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSummary(data);
      toast.success(`Imported ${data.processed} customers from the ERP`);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      loadHistory();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 401) {
        msg = "Your session has expired. Please sign in again.";
      } else if (status === 403) {
        msg = detail || "You don't have permission to import customers.";
      } else if (typeof detail === "string") {
        // Parser / backend errors (400 from parse_erp_workbook, etc.)
        // — surface the real reason so the operator isn't misled with
        // an authentication message.
        msg = detail;
      } else {
        msg = e?.message || "Import failed";
      }
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="customer-import-panel">
      <div className="mb-5 border-b border-gray-200 pb-3">
        <h2 className="text-lg font-semibold text-gray-900">Customer Import</h2>
        <p className="mt-0.5 text-sm text-gray-500">
          Upload the weekly ERP customer export (rpt_278.xls). New customers are added, existing
          customers updated, and customers no longer present in the ERP are marked Inactive.
        </p>
      </div>

      {/* Upload card */}
      <div className="rounded-md border border-gray-200 bg-[#f9fafb] p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-[#1A1A1A] ring-1 ring-gray-200">
              <FileXls size={20} weight="regular" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-700">ERP Customer File</div>
              <div
                data-testid="selected-filename"
                className={`truncate text-[15px] ${file ? "text-gray-900" : "text-gray-400"}`}
              >
                {file ? file.name : "No file selected"}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              ref={inputRef}
              data-testid="customer-import-file-input"
              type="file"
              accept=".xls,.xlsx"
              onChange={onFileChange}
              className="hidden"
            />
            <button
              data-testid="customer-import-select-button"
              onClick={pickFile}
              disabled={uploading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <UploadSimple size={16} weight="bold" /> Select ERP Customer File
            </button>
            <button
              data-testid="customer-import-run-button"
              onClick={runImport}
              disabled={!file || uploading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#EB1D25] px-4 text-sm font-semibold text-white hover:bg-[#AF101B] disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {uploading ? (
                <>
                  <Spinner size={16} className="animate-spin" /> Importing…
                </>
              ) : (
                <>Import Customers</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Last import summary */}
      {summary && (
        <div
          data-testid="customer-import-summary"
          className="mt-5 rounded-md border border-green-200 bg-green-50/60 p-5"
        >
          <div className="mb-4 flex items-center gap-2 text-green-800">
            <CheckCircle size={20} weight="fill" />
            <span className="text-sm font-semibold">Import complete</span>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
            <Stat label="Customers Processed" value={summary.processed} testid="summary-processed" />
            <Stat label="New Customers" value={summary.new_customers} tone="blue" testid="summary-new" />
            <Stat label="Updated Customers" value={summary.updated_customers} tone="amber" testid="summary-updated" />
            <Stat label="Inactive Customers" value={summary.inactive_customers} tone="slate" testid="summary-inactive" />
            <Stat label="Errors" value={summary.errors} tone={summary.errors > 0 ? "red" : "slate"} testid="summary-errors" />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-1 border-t border-green-200 pt-3 text-xs text-gray-600 sm:grid-cols-2">
            <div>
              <span className="font-semibold text-gray-700">Import Date &amp; Time:</span>{" "}
              {formatDateTime(summary.finished_at)}
            </div>
            <div>
              <span className="font-semibold text-gray-700">Imported By:</span> {summary.imported_by}
            </div>
          </div>
          {summary.error_details?.length > 0 && (
            <div className="mt-3 rounded-md border border-red-200 bg-white p-3 text-xs text-red-700">
              <div className="mb-1 flex items-center gap-1 font-semibold">
                <WarningCircle size={14} weight="bold" /> First {summary.error_details.length} errors:
              </div>
              <ul className="ml-4 list-disc space-y-0.5">
                {summary.error_details.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Import history */}
      <div className="mt-8">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600">
          Import History
        </h3>
        <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
          <table data-testid="customer-import-history" className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-[#f3f4f6] text-left text-xs text-gray-600">
                <th className="px-4 py-2.5 font-semibold">Date &amp; Time</th>
                <th className="px-4 py-2.5 font-semibold">Imported By</th>
                <th className="px-4 py-2.5 font-semibold">File</th>
                <th className="px-4 py-2.5 text-right font-semibold">Processed</th>
                <th className="px-4 py-2.5 text-right font-semibold">New</th>
                <th className="px-4 py-2.5 text-right font-semibold">Updated</th>
                <th className="px-4 py-2.5 text-right font-semibold">Inactive</th>
                <th className="px-4 py-2.5 text-right font-semibold">Errors</th>
              </tr>
            </thead>
            <tbody>
              {loadingHistory ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    <Spinner size={16} className="mr-2 inline animate-spin" /> Loading…
                  </td>
                </tr>
              ) : history.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No imports yet.
                  </td>
                </tr>
              ) : (
                history.map((h) => (
                  <tr
                    key={h.id}
                    data-testid={`import-history-row-${h.id}`}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                  >
                    <td className="px-4 py-2.5 font-mono text-gray-700">
                      {formatDateTime(h.finished_at)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-700">{h.imported_by}</td>
                    <td className="px-4 py-2.5 text-gray-700">{h.file_name}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                      {h.processed}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-blue-700">
                      {h.new_customers}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-amber-700">
                      {h.updated_customers}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-500">
                      {h.inactive_customers}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right font-mono ${
                        h.errors > 0 ? "text-[#EB1D25] font-semibold" : "text-gray-500"
                      }`}
                    >
                      {h.errors}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "slate", testid }) {
  const toneCls = {
    slate: "text-gray-900",
    blue: "text-blue-700",
    amber: "text-amber-700",
    red: "text-[#EB1D25]",
  }[tone];
  return (
    <div data-testid={testid}>
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
